// The key dates and next payment shown for each deal on the Pipeline's "All deals" table.
//
// Sales (Sale, Acquisition, Buyside):
//   column 1 = DD / inspection period end, column 2 = closing; next payment = closing.
// Leases (Tenant Rep, LL Rep):
//   column 1 = lease signed (execution), column 2 = commencement; usually two payments,
//   the 1st at lease signing and the 2nd later, labelled "1st half" / "2nd half".
// A payment not marked received is "due" (shown red) from its date onward.

import { underContractDates } from './commission';

export interface PreviewDeal {
  deal_type: string;
  lease_executed_date: string | null;
  commencement_estimated_date: string | null;
  commencement_confirmed_date: string | null;
  contract_effective_date: string | null;
  dd_end_date: string | null;
  closing_date: string | null;
}

export interface PreviewPayment {
  status: string;
  expected_date: string | null;
  slot_key: string | null;
}

export interface KeyDate {
  date: string | null;
  /** True when the date is a default/estimate rather than something entered or confirmed. */
  estimated: boolean;
}

export interface DealPreview {
  first: KeyDate;
  second: KeyDate;
  next: { date: string | null; note: string | null; due: boolean } | null;
  fullyPaid: boolean;
}

const isLease = (t: string) => t === 'tenant_rep' || t === 'll_rep';

function ordinal(i: number, n: number): string | null {
  if (n === 2) return i === 0 ? '1st half' : '2nd half';
  if (n > 2) return `${i + 1} of ${n}`;
  return null;
}

export function dealPreview(d: PreviewDeal, payments: PreviewPayment[], today: string): DealPreview {
  let first: KeyDate;
  let second: KeyDate;
  if (isLease(d.deal_type)) {
    first = { date: d.lease_executed_date, estimated: false };
    second = { date: d.commencement_confirmed_date ?? d.commencement_estimated_date, estimated: !d.commencement_confirmed_date && !!d.commencement_estimated_date };
  } else {
    const uc = underContractDates({ contractEffective: d.contract_effective_date, ddEnd: d.dd_end_date, closing: d.closing_date });
    first = { date: uc.ddEnd, estimated: !d.dd_end_date && !!uc.ddEnd };
    second = { date: uc.closing, estimated: !d.closing_date && !!uc.closing };
  }

  const ordered = [...payments].sort(
    (a, b) => (a.expected_date ?? '9999').localeCompare(b.expected_date ?? '9999') || (a.slot_key ?? '').localeCompare(b.slot_key ?? ''),
  );
  const fullyPaid = ordered.length > 0 && ordered.every((p) => p.status === 'received');
  let next: DealPreview['next'] = null;

  if (!fullyPaid) {
    if (ordered.length) {
      const i = ordered.findIndex((p) => p.status !== 'received');
      const p = ordered[i]!;
      // A payment whose trigger date isn't known yet falls back to the deal's own date.
      const fallback = isLease(d.deal_type) ? (i === 0 ? first.date : second.date) : second.date;
      const date = p.expected_date ?? fallback;
      next = { date, note: isLease(d.deal_type) ? ordinal(i, ordered.length) : null, due: !!date && date <= today };
    } else {
      // No commission entered yet: the rule still gives the date.
      const date = isLease(d.deal_type) ? first.date : second.date;
      if (date) next = { date, note: isLease(d.deal_type) ? '1st half' : null, due: date <= today };
    }
  }
  return { first, second, next, fullyPaid };
}
